<script>
import { computed, ref } from 'vue'
import { formatPrice, vatPrice } from '../../../../../Greativ/helper/price'
import _clone from 'lodash/clone'
import { setSelectedProduct } from '@/store/product'
import { priceFloat, currency } from '@/store/settings'

export default {
  props: {
    id: {
      type: Number,
    },
    name: {
      type: String,
    },
    description: {
      type: String,
    },
    shortDescription: {
      type: String,
    },
    price: {
      type: Number,
    },
    discountPrice: {
      type: Number,
    },
    clientPrice: {
      type: Number,
    },
    vat: {
      type: Number,
      default: 0,
    },
    minAmount: {
      type: Number,
    },
    maxAmount: {
      type: Number,
    },
    amountStep: {
      type: Number,
      default: 1,
    },
    totalInventory: {
      type: Number,
    },
    locked: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const price = computed(() => {
      if (props.clientPrice) {
        return vatPrice(props.clientPrice, props.vat)
      }

      if (props.discountPrice) {
        return vatPrice(props.discountPrice, props.vat)
      }

      return vatPrice(props.price, props.vat)
    })

    const disabled = computed(() => {
      return props.locked
    })

    const amount = ref(0)

    const changeAmount = (value) => {
      let newValue = _clone(amount.value)
      if (isNaN(newValue)) {
        newValue = 1
      }
      const step = props.amountStep || 1

      if (value === 'inc') {
        newValue += step
      } else if (value === 'dec') {
        newValue -= step
      } else if (step && newValue % step !== 0) {
        newValue += step - (newValue % step)
      }

      let min = props.minAmount || 0
      if (step && min % step !== 0) {
        min += step - (min % step)
      }

      let max = props.maxAmount

      if (max > props.totalInventory) {
        max = props.totalInventory
      } else if (!max) {
        max = props.totalInventory
      }

      if (step && max % step !== 0) {
        max -= max % step
      }

      if (newValue < min) {
        newValue = min
      }

      if (newValue > max) {
        newValue = max
      }

      amount.value = newValue

      setSelectedProduct(props.id, newValue)
    }

    const showDescription = ref(false)

    const description = computed(() => {
      let desc = ''

      if (props.shortDescription) {
        desc += props.shortDescription
      }

      if (props.description) {
        desc += props.description
      }

      return desc
    })

    return {
      description,
      showDescription,
      price,
      disabled,
      amount,
      changeAmount,
      formatPrice,
      priceFloat,
      currency,
    }
  },
}
</script>

<template>
  <div class="py-2">
    <div class="flex justify-between items-center mb-6 lg:mb-3">
      <h3 class="text-8 uppercase mb-0">{{ name }}</h3>

      <button
        type="button"
        class="
          flex-shrink-0
          inline-flex
          justify-center
          items-center
          w-12
          h-12
          text-8 text-white text-opacity-80
          bg-blue-900 bg-opacity-50
          rounded-full
          ml-4
          transition-colors
          duration-300
          hover:bg-blue-hover
          focus:outline-none
          lg:w-8 lg:h-8 lg:text-5.5
        "
        @click="showDescription = !showDescription"
        v-if="description"
      >
        ?
      </button>
    </div>

    <div
      class="-mt-5 mb-4 lg:-mt-2"
      v-html="description"
      v-if="showDescription"
    ></div>

    <div class="flex justify-between items-center">
      <div class="text-12 font-bold">
        {{ `${currency} ${formatPrice(price, priceFloat)}` }}
      </div>

      <div class="flex items-center text-14px font-medium uppercase w-72">
        <button
          type="button"
          class="
            flex-shrink-0
            w-16
            h-16
            text-12 text-blue-900
            bg-yellow
            rounded-full
            leading-none
            transition-colors
            duration-300
            hover:bg-yellow-hover
            mr-3
          "
          :disabled="disabled"
          @click="changeAmount('dec')"
        >
          <span class="block -mt-2px">-</span>
        </button>
        <input
          type="number"
          inputmode="numeric"
          class="
            w-full
            h-16
            bg-white
            text-center
            font-bold
            rounded-full
            number-appearance-none
            focus:outline-none
          "
          :disabled="disabled"
          v-model="amount"
          @change="changeAmount($event.target.value)"
        />
        <button
          type="button"
          class="
            flex-shrink-0
            w-16
            h-16
            text-12 text-blue-900
            bg-yellow
            rounded-full
            leading-none
            transition-colors
            duration-300
            hover:bg-yellow-hover
            ml-3
          "
          :disabled="disabled"
          @click="changeAmount('inc')"
        >
          <span class="block -mt-2px">+</span>
        </button>
      </div>
    </div>
  </div>
</template>
